/*
 * Cloud Functions to be implemented:
 *
 * 1. sendAvailabilityRequest(menteeEmail, mentorName)
 *    - Sends email to mentee asking them to input their availability
 *    - Email should include link to availability selection interface
 *
 * 2. scheduleMeeting(attendeeEmails, dayOfWeek, hour, meetingTitle)
 *    - Creates Google Meet invitation
 *    - Sends calendar invites to all attendees
 *    - Returns meeting link and details
 */

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import VideocamIcon from '@mui/icons-material/Videocam';
import { Box, Button, Checkbox, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, List, ListItem, ListItemText, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, useTheme } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CancelIcon from '@mui/icons-material/Cancel';
import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Context as AppContext } from "../../context/AppContext";
import { MainLayout } from "../../layouts/main-layout/MainLayout";
import { capitalizeFirstWord } from '../../utils/functions';
import './mentorHomepage.css';
import { enums } from '../../utils/enums';

const Internship = ({ isMentor = true }) => {
    const theme = useTheme();
    const { state: { currentUser, interns, internships, mentors }, updateInternship, getStudents, getInternships, sendAvailabilityRequest, scheduleMeeting, cancelMeeting } = useContext(AppContext);
    const { id } = useParams()
    const [internship, setInternship] = useState(null)
    const [student, setStudent] = useState(null)
    const [title, setTitle] = useState(internship?.title || '');
    const [newTask, setNewTask] = useState('');
    const [newGoal, setNewGoal] = useState('');
    const [localTitle, setLocalTitle] = useState('');
    const [newJournalEntry, setNewJournalEntry] = useState('');
    const [availability, setAvailability] = useState({});
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState({ day: '', hour: 0, time: '' });
    const [meetingDate, setMeetingDate] = useState('');
    const [meetingTitle, setMeetingTitle] = useState('');
    const [schedulingExpanded, setSchedulingExpanded] = useState(false);
    const [isSchedulingMeeting, setIsSchedulingMeeting] = useState(false);
    const navigate = useNavigate()
    useEffect(() => {
       if(!internships || internships?.length === 0){
        navigate('/mentor')
       }
    }, [currentUser])

    useEffect(() => {
        if (internships?.length > 0) {
            const internship = internships.find(internship => internship.uid === id)
            setInternship(internship)
            setLocalTitle(internship?.title || '')
            setAvailability(internship?.availability || {})
            const intern = interns.find(intern => intern.uid === internship.studentUid)
            setStudent(intern)
        }
    }, [internships])

    const handleTitleChange = (event) => {
        setLocalTitle(event.target.value);
    };

    const handleTitleBlur = () => {
        if (localTitle !== internship?.title) {
            const updatedInternship = {
                ...internship,
                title: localTitle
            };
            setInternship(updatedInternship);
            updateInternship(updatedInternship);
        }
    };

    const handleAddTask = () => {
        if (!newTask.trim()) return;

        const newTaskObj = {
            id: Date.now(),
            description: newTask,
            completed: false,
            authorUid: currentUser.uid,
            authorName: currentUser.name,
            createdAt: Date.now()
        };

        const updatedInternship = {
            ...internship,
            tasks: [...(internship.tasks || []), newTaskObj]
        };

        setInternship(updatedInternship);
        updateInternship(updatedInternship);
        setNewTask('');
    };

    const handleDeleteTask = (taskId) => {
        const updatedInternship = {
            ...internship,
            tasks: internship.tasks.filter(task => task.id !== taskId)
        };

        setInternship(updatedInternship);
        updateInternship(updatedInternship);
    };

    const handleAddGoal = () => {
        if (!newGoal.trim()) return;

        const newGoalObj = {
            id: Date.now(),
            description: newGoal,
            completed: false,
            authorUid: currentUser.uid,
            authorName: currentUser.name,
            createdAt: Date.now()
        };

        const updatedInternship = {
            ...internship,
            goals: [...(internship.goals || []), newGoalObj]
        };

        setInternship(updatedInternship);
        updateInternship(updatedInternship);
        setNewGoal('');
    };

    const handleDeleteGoal = (goalId) => {
        const updatedInternship = {
            ...internship,
            goals: internship.goals.filter(goal => goal.id !== goalId)
        };

        setInternship(updatedInternship);
        updateInternship(updatedInternship);
    };

    const handleAddJournalEntry = (e) => {
        e.preventDefault();
        if (!newJournalEntry.trim()) return;

        const newEntry = {
            id: Date.now(),
            content: newJournalEntry,
            timestamp: Date.now(),
            authorUid: currentUser.uid,
            authorName: currentUser.name
        };

        const updatedInternship = {
            ...internship,
            journalEntries: [...(internship.journalEntries || []), newEntry]
        };

        setInternship(updatedInternship);
        updateInternship(updatedInternship);
        setNewJournalEntry('');
    };

    const getWelcomeNames = () => {
        const currentUserRole = currentUser?.role;
        const isCurrentUserMentor = currentUserRole === enums.MENTOR;

        if (isCurrentUserMentor) {
            return (
                <>
                    <span className={'rainbow-text'}> {capitalizeFirstWord(currentUser?.name)} </span>
                    and
                    <span className={'rainbow-text'}> {capitalizeFirstWord(interns.filter(intern => intern.uid === internship?.studentUid)[0]?.name)} </span>
                </>
            );
        }
        else {
            return (
                <>
                    <span className={'rainbow-text'}> {capitalizeFirstWord(currentUser?.name)} </span>
                    and
                    <span className={'rainbow-text'}> {capitalizeFirstWord(mentors.filter(mentor => mentor.uid === internship?.mentorUid)[0]?.name)} </span>
                </>
            );
        }
    };

    const handleRequestAvailability = async () => {
        try {
            console.log('Sending availability request email to mentee...');
            const mentee = interns.find(intern => intern.uid === internship?.studentUid);
            if (mentee && mentee.email) {
                await sendAvailabilityRequest(mentee.email, mentee.name, currentUser.name);
            } else {
                console.log('Mentee email not found, showing generic message');
                console.log('Availability request sent to mentee!');
            }
        } catch (error) {
            console.error('Error sending availability request:', error);
            console.error('Failed to send availability request. Please try again.');
        }
    };

    const generateTimeSlots = () => {
        const slots = [];
        for (let hour = 7; hour <= 19; hour++) {
            const time12 = hour > 12 ? `${hour - 12}:00 PM` : hour === 12 ? '12:00 PM' : `${hour}:00 AM`;
            slots.push({ hour, display: time12 });
        }
        return slots;
    };

    const getDaysOfWeek = () => ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    const getScheduledMeeting = (day, hour) => {
        return internship?.scheduledMeetings?.find(meeting =>
            meeting.day === day && meeting.hour === hour
        );
    };

    const handleAvailabilityToggle = (day, hour) => {
        const key = `${day}-${hour}`;
        const newAvailability = {
            ...availability,
            [key]: !availability[key]
        };
        setAvailability(newAvailability);

        // Update database with new availability
        const updatedInternship = {
            ...internship,
            availability: newAvailability
        };
        setInternship(updatedInternship);
        updateInternship(updatedInternship);
    };

    const handleScheduleMeeting = (day, hour) => {
        const time12 = hour > 12 ? `${hour - 12}:00 PM` : hour === 12 ? '12:00 PM' : `${hour}:00 AM`;
        setSelectedSlot({ day, hour, time: time12 });
        const mentorName = currentUser?.name || 'Mentor';
        setMeetingTitle(`AIDEN Industry Horizons: ${student?.name || 'Mentee'} with ${mentorName}`);

        // Set default date to next occurrence of the selected day
        const today = new Date();
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const targetDayIndex = dayNames.indexOf(day);
        const daysUntilTarget = (targetDayIndex - today.getDay() + 7) % 7 || 7;
        const targetDate = new Date(today);
        targetDate.setDate(today.getDate() + daysUntilTarget);
        setMeetingDate(targetDate.toISOString().split('T')[0]);

        setModalOpen(true);
    };

    const handleConfirmMeeting = async () => {
        setIsSchedulingMeeting(true);
        try {
            console.log(`Scheduling meeting for ${selectedSlot.day} at ${selectedSlot.time} on ${meetingDate}`);

            const mentee = interns.find(intern => intern.uid === internship?.studentUid);
            const mentor = mentors.find(mentor => mentor.uid === internship?.mentorUid) || currentUser;

            if (!mentee) {
                console.error('Error: Cannot find mentee information. Please ensure the student is properly registered.');
                setIsSchedulingMeeting(false);
                return;
            }

            if (!mentee.email) {
                console.error('Error: Mentee email address is missing. Cannot schedule meeting without valid email.');
                setIsSchedulingMeeting(false);
                return;
            }

            if (!mentor) {
                console.error('Error: Cannot find mentor information.');
                setIsSchedulingMeeting(false);
                return;
            }

            if (!mentor.email && !currentUser.email) {
                console.error('Error: Mentor email address is missing. Cannot schedule meeting without valid email.');
                setIsSchedulingMeeting(false);
                return;
            }

            // Get user's timezone
            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            console.log(userTimezone)
            const result = await scheduleMeeting(
                mentor.email || currentUser.email,
                mentee.email,
                meetingTitle,
                meetingDate,
                selectedSlot.day,
                selectedSlot.hour,
                mentor.name || currentUser.name,
                mentee.name,
                userTimezone
            );

            console.log('Meeting scheduled successfully:', result);
            console.log(`Meeting "${meetingTitle}" scheduled successfully for ${selectedSlot.day} at ${selectedSlot.time} on ${meetingDate}!`);

            // Record the meeting in the database
            const meetingRecord = {
                id: Date.now(),
                day: selectedSlot.day,
                hour: selectedSlot.hour,
                date: meetingDate,
                time: selectedSlot.time,
                title: meetingTitle,
                eventId: result.eventId,
                meetingLink: result.meetingLink,
                createdAt: Date.now(),
                createdBy: currentUser.uid
            };

            // Check if this is the first meeting
            const isFirstMeeting = !internship.scheduledMeetings || internship.scheduledMeetings.length === 0;

            let updatedInternship = {
                ...internship,
                scheduledMeetings: [...(internship.scheduledMeetings || []), meetingRecord]
            };

            // If this is the first meeting, set the end date to 8 weeks from the meeting date
            if (isFirstMeeting) {
                const meetingDateObj = new Date(meetingDate);
                const endDate = new Date(meetingDateObj);
                endDate.setDate(meetingDateObj.getDate() + (8 * 7)); // Add 8 weeks (56 days)

                updatedInternship = {
                    ...updatedInternship,
                    startDate: meetingDate,
                    endDate: endDate.toISOString()
                };

                console.log(`First meeting scheduled! Setting internship duration:`);
                console.log(`Start date: ${meetingDate}`);
                console.log(`End date (8 weeks later): ${endDate.toISOString()}`);
            }

            setInternship(updatedInternship);
            updateInternship(updatedInternship);

            setModalOpen(false);
        } catch (error) {
            console.error('Error scheduling meeting:', error);
            console.error('Failed to schedule meeting. Please try again.');
        } finally {
            setIsSchedulingMeeting(false);
        }
    };

    const handleCancelMeeting = async (meeting) => {
        if (currentUser?.role !== enums.MENTOR) {
            console.error('Only mentors can cancel meetings');
            return;
        }

        try {
            console.log('Cancelling meeting:', meeting);

            // Cancel the Google Calendar event
            await cancelMeeting(meeting.eventId);

            // Remove meeting from database
            const updatedMeetings = internship.scheduledMeetings.filter(m => m.id !== meeting.id);
            const updatedInternship = {
                ...internship,
                scheduledMeetings: updatedMeetings
            };

            setInternship(updatedInternship);
            updateInternship(updatedInternship);

            console.log(`Meeting "${meeting.title}" cancelled successfully`);
        } catch (error) {
            console.error('Error cancelling meeting:', error);
            console.error('Failed to cancel meeting. Please try again.');
        }
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedSlot({ day: '', hour: 0, time: '' });
        setMeetingDate('');
        setMeetingTitle('');
        setIsSchedulingMeeting(false);
    };

    const handleMarkComplete = () => {
        const isMentor = currentUser?.role === enums.MENTOR;
        const updatedInternship = {
            ...internship,
            [isMentor ? 'mentorComplete' : 'internComplete']: true,
            [`${isMentor ? 'mentor' : 'intern'}CompletedAt`]: Date.now()
        };

        setInternship(updatedInternship);
        updateInternship(updatedInternship);

        console.log(`${isMentor ? 'Mentor' : 'Intern'} marked mentorship as complete`);
    };

    return (
        <MainLayout>
            <Box sx={{
                minHeight: '100vh',
                bgcolor: '#fafbfc',
                fontFamily: 'Poppins, system-ui, -apple-system, sans-serif'
            }}>
                <Box sx={{
                    maxWidth: '1200px',
                    mx: 'auto',
                    px: { xs: 3, md: 4 },
                    py: { xs: 3, md: 5 }
                }}>
                    {/* Welcome Header */}
                    <Box sx={{ mb: 5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                            <Box>
                                <Typography
                                    variant="h4"
                                    sx={{
                                        fontSize: { xs: '1.5rem', md: '2rem' },
                                        fontWeight: 600,
                                        color: '#1a1a1a',
                                        mb: 1,
                                        letterSpacing: '-0.02em'
                                    }}
                                >
                                    Welcome, {getWelcomeNames()}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: '#64748b',
                                        fontSize: '1rem',
                                        maxWidth: '600px'
                                    }}
                                >
                                    8-week intensive mentorship program
                                </Typography>
                            </Box>

                            {/* Completion Status and Button */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                                {/* Status Indicators */}
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                    {internship?.mentorComplete && (
                                        <Chip
                                            label="Mentor Completed"
                                            color="success"
                                            size="small"
                                            sx={{ fontWeight: 500 }}
                                        />
                                    )}
                                    {internship?.internComplete && (
                                        <Chip
                                            label="Intern Completed"
                                            color="success"
                                            size="small"
                                            sx={{ fontWeight: 500 }}
                                        />
                                    )}
                                </Box>

                                {/* Mark Complete Button */}
                                {!((currentUser?.role === enums.MENTOR && internship?.mentorComplete) ||
                                    (currentUser?.role === enums.INTERN && internship?.internComplete)) && (
                                    <Button
                                        variant="contained"
                                        onClick={handleMarkComplete}
                                        sx={{
                                            bgcolor: '#10b981',
                                            color: 'white',
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            px: 3,
                                            py: 1.25,
                                            borderRadius: '12px',
                                            boxShadow: '0 4px 14px 0 rgba(16, 185, 129, 0.4)',
                                            '&:hover': {
                                                bgcolor: '#059669',
                                                boxShadow: '0 6px 20px 0 rgba(16, 185, 129, 0.5)',
                                                transform: 'translateY(-1px)'
                                            },
                                            transition: 'all 0.2s ease-in-out'
                                        }}
                                    >
                                        Mark Mentorship as Complete
                                    </Button>
                                )}

                                {/* Both Completed Message */}
                                {internship?.mentorComplete && internship?.internComplete && (
                                    <Box sx={{
                                        bgcolor: '#d1fae5',
                                        border: '2px solid #10b981',
                                        borderRadius: '12px',
                                        px: 3,
                                        py: 2,
                                        mt: 1
                                    }}>
                                        <Typography variant="body1" sx={{ color: '#065f46', fontWeight: 600, textAlign: 'center' }}>
                                            🎉 Mentorship Completed Successfully!
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    {/* Internship Title Section */}
                    <Box sx={{
                        bgcolor: 'white',
                        borderRadius: '16px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
                        p: { xs: 3, md: 4 },
                        mb: 4,
                        transition: 'box-shadow 0.2s ease-in-out',
                        '&:hover': {
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                        }
                    }}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                color: '#1e293b',
                                fontSize: '1.125rem',
                                mb: 3
                            }}
                        >
                            Mentorship Title
                        </Typography>
                        <TextField
                            placeholder="Enter a meaningful title for this mentorship..."
                            value={localTitle}
                            onChange={handleTitleChange}
                            onBlur={handleTitleBlur}
                            variant="outlined"
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '12px',
                                    fontSize: '1rem',
                                    '&:hover fieldset': {
                                        borderColor: theme.palette.primary.main
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: theme.palette.primary.main,
                                        borderWidth: '2px'
                                    }
                                }
                            }}
                            InputProps={{
                                notched: false,
                            }}
                            InputLabelProps={{
                                shrink: false,
                            }}
                        />
                    </Box>

                    {/* Meeting Scheduling Section */}
                    <Box sx={{
                        bgcolor: 'white',
                        borderRadius: '16px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
                        mb: 4,
                        transition: 'box-shadow 0.2s ease-in-out',
                        '&:hover': {
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                        }
                    }}>
                        {/* Collapsible Header */}
                        <Box
                            onClick={() => setSchedulingExpanded(!schedulingExpanded)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: { xs: 3, md: 4 },
                                cursor: 'pointer',
                                borderRadius: '16px',
                                transition: 'background-color 0.2s ease-in-out',
                                '&:hover': {
                                    bgcolor: '#f8fafc'
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <CalendarTodayIcon sx={{ color: '#059669', fontSize: '1.5rem' }} />
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 600,
                                        color: '#1e293b',
                                        fontSize: '1.125rem'
                                    }}
                                >
                                    Meeting Scheduling
                                </Typography>
                            </Box>
                            <IconButton
                                sx={{
                                    transition: 'transform 0.2s ease-in-out',
                                    transform: schedulingExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                                }}
                            >
                                <ExpandMoreIcon />
                            </IconButton>
                        </Box>

                        {/* Collapsible Content */}
                        <Collapse in={schedulingExpanded}>
                            <Box sx={{ px: { xs: 3, md: 4 }, pb: { xs: 3, md: 4 } }}>

                        {currentUser?.role === enums.MENTOR ? (
                            <>
                                <Typography
                                    variant="body2"
                                    sx={{ color: '#64748b', mb: 3 }}
                                >
                                    Click on a green cell to schedule a meeting in available time slot.
                                </Typography>

                                <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: '12px' }}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc' }}>Time</TableCell>
                                                {getDaysOfWeek().map(day => (
                                                    <TableCell key={day} sx={{ fontWeight: 600, bgcolor: '#f8fafc', textAlign: 'center' }}>
                                                        {day.substring(0, 3)}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {generateTimeSlots().map(({ hour, display }) => (
                                                <TableRow key={hour}>
                                                    <TableCell sx={{ fontWeight: 500 }}>{display}</TableCell>
                                                    {getDaysOfWeek().map(day => {
                                                        const isAvailable = availability[`${day}-${hour}`];
                                                        const scheduledMeeting = getScheduledMeeting(day, hour);
                                                        return (
                                                            <TableCell key={`${day}-${hour}`} sx={{ textAlign: 'center', p: 1 }}>
                                                                {scheduledMeeting ? (
                                                                    <Box sx={{
                                                                        position: 'relative',
                                                                        display: 'inline-block'
                                                                    }}>
                                                                        <Button
                                                                            size="small"
                                                                            sx={{
                                                                                minWidth: '40px',
                                                                                height: '40px',
                                                                                bgcolor: '#3b82f6',
                                                                                color: 'white',
                                                                                borderRadius: '8px',
                                                                                position: 'relative',
                                                                                '&:hover': {
                                                                                    bgcolor: '#2563eb'
                                                                                }
                                                                            }}
                                                                            title={`Meeting: ${scheduledMeeting.title}`}
                                                                        >
                                                                            <CalendarMonthIcon fontSize="small" />
                                                                            {currentUser?.role === enums.MENTOR && (
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleCancelMeeting(scheduledMeeting);
                                                                                    }}
                                                                                    sx={{
                                                                                        position: 'absolute',
                                                                                        top: '-8px',
                                                                                        right: '-8px',
                                                                                        width: '18px',
                                                                                        height: '18px',
                                                                                        bgcolor: '#dc2626',
                                                                                        color: 'white',
                                                                                        borderRadius: '50%',
                                                                                        border: '2px solid white',
                                                                                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                                                                        zIndex: 1,
                                                                                        '&:hover': {
                                                                                            bgcolor: '#b91c1c',
                                                                                            transform: 'scale(1.15)'
                                                                                        },
                                                                                        transition: 'all 0.2s ease'
                                                                                    }}
                                                                                    title="Cancel Meeting"
                                                                                >
                                                                                    <CancelIcon sx={{ fontSize: '12px' }} />
                                                                                </IconButton>
                                                                            )}
                                                                        </Button>
                                                                    </Box>
                                                                ) : isAvailable ? (
                                                                    <Button
                                                                        size="small"
                                                                        onClick={() => handleScheduleMeeting(day, hour)}
                                                                        sx={{
                                                                            minWidth: '40px',
                                                                            height: '40px',
                                                                            bgcolor: '#10b981',
                                                                            color: 'white',
                                                                            borderRadius: '8px',
                                                                            margin: '0 auto',
                                                                            display: 'flex',
                                                                            '&:hover': {
                                                                                bgcolor: '#059669'
                                                                            }
                                                                        }}
                                                                    >
                                                                        <VideocamIcon fontSize="small" />
                                                                    </Button>
                                                                ) : (
                                                                    <Box sx={{
                                                                        width: '40px',
                                                                        height: '40px',
                                                                        bgcolor: '#f3f4f6',
                                                                        borderRadius: '8px',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        margin: '0 auto'
                                                                    }}>
                                                                        -
                                                                    </Box>
                                                                )}
                                                            </TableCell>
                                                        );
                                                    })}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </>
                        ) : (
                            <>
                                <Typography
                                    variant="body2"
                                    sx={{ color: '#64748b', mb: 3 }}
                                >
                                    Select your available time slots for meetings with your mentor. Click the checkboxes to mark when you're available.
                                </Typography>

                                <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: '12px' }}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc' }}>Time</TableCell>
                                                {getDaysOfWeek().map(day => (
                                                    <TableCell key={day} sx={{ fontWeight: 600, bgcolor: '#f8fafc', textAlign: 'center' }}>
                                                        {day.substring(0, 3)}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {generateTimeSlots().map(({ hour, display }) => (
                                                <TableRow key={hour}>
                                                    <TableCell sx={{ fontWeight: 500 }}>{display}</TableCell>
                                                    {getDaysOfWeek().map(day => {
                                                        const isSelected = availability[`${day}-${hour}`];
                                                        return (
                                                            <TableCell key={`${day}-${hour}`} sx={{ textAlign: 'center', p: 1 }}>
                                                                <Box
                                                                    sx={{
                                                                        width: '40px',
                                                                        height: '40px',
                                                                        bgcolor: isSelected ? '#10b981' : '#f3f4f6',
                                                                        borderRadius: '8px',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        margin: '0 auto',
                                                                        cursor: 'pointer',
                                                                        transition: 'all 0.2s ease',
                                                                        '&:hover': {
                                                                            bgcolor: isSelected ? '#059669' : '#e5e7eb',
                                                                            transform: 'scale(1.05)'
                                                                        }
                                                                    }}
                                                                >
                                                                    <Checkbox
                                                                        checked={isSelected || false}
                                                                        onChange={(e) => {
                                                                            e.stopPropagation();
                                                                            handleAvailabilityToggle(day, hour);
                                                                        }}
                                                                        sx={{
                                                                            color: 'white',
                                                                            '&.Mui-checked': {
                                                                                color: 'white'
                                                                            },
                                                                            p: 0
                                                                        }}
                                                                    />
                                                                </Box>
                                                            </TableCell>
                                                        );
                                                    })}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </>
                        )}
                            </Box>
                        </Collapse>
                    </Box>

                    {/* Goals and Tasks Grid */}
                    <Grid container spacing={4} sx={{ mb: 4 }}>
                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                bgcolor: 'white',
                                borderRadius: '16px',
                                border: '1px solid #e2e8f0',
                                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
                                p: { xs: 2, md: 3 },
                                height: 'fit-content',
                                transition: 'box-shadow 0.2s ease-in-out',
                                '&:hover': {
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                                }
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 3,
                                    flexWrap: 'wrap',
                                    gap: 2
                                }}>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontWeight: 600,
                                            color: '#1e293b',
                                            fontSize: '1.25rem'
                                        }}
                                    >
                                        Goals
                                    </Typography>
                                    <Box sx={{
                                        px: 2,
                                        py: 0.5,
                                        bgcolor: '#f0f9ff',
                                        color: '#0c4a6e',
                                        borderRadius: '20px',
                                        fontSize: '0.875rem',
                                        fontWeight: 500
                                    }}>
                                        {internship?.goals?.length || 0} {(internship?.goals?.length || 0) === 1 ? 'Goal' : 'Goals'}
                                    </Box>
                                </Box>

                                <Stack direction="column" spacing={2} sx={{ mb: 3 }}>
                                    <TextField
                                        placeholder="Add a new goal..."
                                        value={newGoal}
                                        onChange={(e) => setNewGoal(e.target.value)}
                                        variant="outlined"
                                        fullWidth
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '12px',
                                                fontSize: '0.95rem',
                                                '&:hover fieldset': {
                                                    borderColor: theme.palette.primary.main
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: theme.palette.primary.main,
                                                    borderWidth: '2px'
                                                }
                                            }
                                        }}
                                        InputProps={{
                                            notched: false,
                                        }}
                                        InputLabelProps={{
                                            shrink: false,
                                        }}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                handleAddGoal();
                                            }
                                        }}
                                    />
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={handleAddGoal}
                                        disabled={!newGoal.trim()}
                                        sx={{
                                            bgcolor: theme.palette.primary.main,
                                            color: 'white',
                                            fontSize: '0.875rem',
                                            fontWeight: 600,
                                            px: 3,
                                            py: 1.25,
                                            borderRadius: '12px',
                                            textTransform: 'none',
                                            alignSelf: 'flex-start',
                                            boxShadow: `0 4px 14px 0 ${theme.palette.primary.main}40`,
                                            '&:hover': {
                                                bgcolor: theme.palette.primary.dark || '#4c2a6b',
                                                boxShadow: `0 6px 20px 0 ${theme.palette.primary.main}59`,
                                                transform: 'translateY(-1px)'
                                            },
                                            '&:disabled': {
                                                bgcolor: '#e5e7eb',
                                                color: '#9ca3af',
                                                transform: 'none',
                                                boxShadow: 'none'
                                            },
                                            transition: 'all 0.2s ease-in-out'
                                        }}
                                    >
                                        Add Goal
                                    </Button>
                                </Stack>

                                {/* Goals List */}
                                <Box>
                                    {internship?.goals?.map((goal, index) => (
                                        <Box
                                            key={goal.id}
                                            sx={{
                                                bgcolor: '#f8fafc',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '12px',
                                                p: 2.5,
                                                mb: 2,
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                transition: 'all 0.2s ease-in-out',
                                                '&:hover': {
                                                    bgcolor: '#f1f5f9',
                                                    borderColor: '#cbd5e1',
                                                    transform: 'translateY(-1px)',
                                                    boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.08)'
                                                }
                                            }}
                                        >
                                            <Box sx={{ flex: 1 }}>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        color: '#374151',
                                                        fontWeight: 500,
                                                        mb: 0.5
                                                    }}
                                                >
                                                    {index + 1}. {goal.description}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: '#6b7280',
                                                        fontSize: '0.75rem',
                                                        fontStyle: 'italic'
                                                    }}
                                                >
                                                    Added by {goal.authorName} {goal.createdAt && `• ${new Date(goal.createdAt).toLocaleDateString()}`}
                                                </Typography>
                                            </Box>
                                            <IconButton
                                                onClick={() => handleDeleteGoal(goal.id)}
                                                size="small"
                                                sx={{
                                                    color: '#dc2626',
                                                    '&:hover': {
                                                        bgcolor: '#fef2f2',
                                                        transform: 'scale(1.1)'
                                                    },
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                    {(!internship?.goals || internship?.goals?.length === 0) && (
                                        <Box sx={{
                                            textAlign: 'center',
                                            py: 4,
                                            color: '#6b7280',
                                            fontStyle: 'italic'
                                        }}>
                                            <Typography>No goals added yet. Start by adding your first goal!</Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                bgcolor: 'white',
                                borderRadius: '16px',
                                border: '1px solid #e2e8f0',
                                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
                                p: { xs: 2, md: 3 },
                                height: 'fit-content',
                                transition: 'box-shadow 0.2s ease-in-out',
                                '&:hover': {
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                                }
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 3,
                                    flexWrap: 'wrap',
                                    gap: 2
                                }}>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontWeight: 600,
                                            color: '#1e293b',
                                            fontSize: '1.25rem'
                                        }}
                                    >
                                        Tasks
                                    </Typography>
                                    <Box sx={{
                                        px: 2,
                                        py: 0.5,
                                        bgcolor: '#f0f9ff',
                                        color: theme.palette.accent.main,
                                        borderRadius: '20px',
                                        fontSize: '0.875rem',
                                        fontWeight: 500
                                    }}>
                                        {internship?.tasks?.length || 0} {(internship?.tasks?.length || 0) === 1 ? 'Task' : 'Tasks'}
                                    </Box>
                                </Box>

                                <Stack direction="column" spacing={2} sx={{ mb: 3 }}>
                                    <TextField
                                        placeholder="Add a new task..."
                                        value={newTask}
                                        onChange={(e) => setNewTask(e.target.value)}
                                        variant="outlined"
                                        fullWidth
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '12px',
                                                fontSize: '0.95rem',
                                                '&:hover fieldset': {
                                                    borderColor: theme.palette.primary.main
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: theme.palette.primary.main,
                                                    borderWidth: '2px'
                                                }
                                            }
                                        }}
                                        InputProps={{
                                            notched: false,
                                        }}
                                        InputLabelProps={{
                                            shrink: false,
                                        }}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                handleAddTask();
                                            }
                                        }}
                                    />
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={handleAddTask}
                                        disabled={!newTask.trim()}
                                        sx={{
                                            bgcolor: theme.palette.primary.main,
                                            color: 'white',
                                            fontSize: '0.875rem',
                                            fontWeight: 600,
                                            px: 3,
                                            py: 1.25,
                                            borderRadius: '12px',
                                            textTransform: 'none',
                                            alignSelf: 'flex-start',
                                            boxShadow: `0 4px 14px 0 ${theme.palette.primary.main}40`,
                                            '&:hover': {
                                                bgcolor: theme.palette.primary.dark || '#4c2a6b',
                                                boxShadow: `0 6px 20px 0 ${theme.palette.primary.main}59`,
                                                transform: 'translateY(-1px)'
                                            },
                                            '&:disabled': {
                                                bgcolor: '#e5e7eb',
                                                color: '#9ca3af',
                                                transform: 'none',
                                                boxShadow: 'none'
                                            },
                                            transition: 'all 0.2s ease-in-out'
                                        }}
                                    >
                                        Add Task
                                    </Button>
                                </Stack>

                                {/* Tasks List */}
                                <Box>
                                    {internship?.tasks?.map((task, index) => (
                                        <Box
                                            key={task.id}
                                            sx={{
                                                bgcolor: '#f8fafc',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '12px',
                                                p: 2.5,
                                                mb: 2,
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                transition: 'all 0.2s ease-in-out',
                                                '&:hover': {
                                                    bgcolor: '#f1f5f9',
                                                    borderColor: '#cbd5e1',
                                                    transform: 'translateY(-1px)',
                                                    boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.08)'
                                                }
                                            }}
                                        >
                                            <Box sx={{ flex: 1 }}>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        color: '#374151',
                                                        fontWeight: 500,
                                                        mb: 0.5
                                                    }}
                                                >
                                                    {index + 1}. {task.description}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: '#6b7280',
                                                        fontSize: '0.75rem',
                                                        fontStyle: 'italic'
                                                    }}
                                                >
                                                    Added by {task.authorName} {task.createdAt && `• ${new Date(task.createdAt).toLocaleDateString()}`}
                                                </Typography>
                                            </Box>
                                            <IconButton
                                                onClick={() => handleDeleteTask(task.id)}
                                                size="small"
                                                sx={{
                                                    color: '#dc2626',
                                                    '&:hover': {
                                                        bgcolor: '#fef2f2',
                                                        transform: 'scale(1.1)'
                                                    },
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                    {(!internship?.tasks || internship?.tasks?.length === 0) && (
                                        <Box sx={{
                                            textAlign: 'center',
                                            py: 4,
                                            color: '#6b7280',
                                            fontStyle: 'italic'
                                        }}>
                                            <Typography>No tasks added yet. Start by adding your first task!</Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>

                    {/* Journal Section - Only for Interns */}
                    {currentUser.role === enums.INTERN && (
                        <Box sx={{
                            bgcolor: 'white',
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
                            p: { xs: 3, md: 4 },
                            transition: 'box-shadow 0.2s ease-in-out',
                            '&:hover': {
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                            }
                        }}>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 3,
                                flexWrap: 'wrap',
                                gap: 2
                            }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 600,
                                        color: '#1e293b',
                                        fontSize: '1.125rem'
                                    }}
                                >
                                    Reflection Journal
                                </Typography>
                                <Box sx={{
                                    px: 2,
                                    py: 0.5,
                                    bgcolor: '#fef3c7',
                                    color: '#92400e',
                                    borderRadius: '20px',
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5
                                }}>
                                    Private
                                </Box>
                            </Box>

                            <Typography
                                variant="body1"
                                sx={{
                                    color: '#64748b',
                                    fontSize: '1rem',
                                    lineHeight: 1.6,
                                    mb: 4
                                }}
                            >
                                Use this space to reflect on your meetings with your mentor. Record key insights,
                                action items, and personal growth moments. Try to journal after each meeting while
                                the conversation is still fresh in your mind.
                            </Typography>

                            <Box component="form" onSubmit={handleAddJournalEntry} sx={{ mb: 4 }}>
                                <TextField
                                    label="New Journal Entry"
                                    placeholder="What did you learn today? What actions will you take before the next meeting? How are you feeling about your progress?"
                                    multiline
                                    rows={5}
                                    fullWidth
                                    value={newJournalEntry}
                                    onChange={(e) => setNewJournalEntry(e.target.value)}
                                    variant="outlined"
                                    sx={{
                                        mb: 3,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '12px',
                                            fontSize: '0.95rem',
                                            '&:hover fieldset': {
                                                borderColor: theme.palette.primary.main
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme.palette.primary.main,
                                                borderWidth: '2px'
                                            }
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: '#374151',
                                            fontWeight: 500,
                                            '&.Mui-focused': {
                                                color: theme.palette.primary.main
                                            }
                                        }
                                    }}
                                />
                                <Button
                                    variant="contained"
                                    type="submit"
                                    startIcon={<AddIcon />}
                                    disabled={!newJournalEntry.trim()}
                                    sx={{
                                        bgcolor: theme.palette.primary.main,
                                        color: 'white',
                                        fontSize: '0.875rem',
                                        fontWeight: 600,
                                        px: 3,
                                        py: 1.25,
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        boxShadow: `0 4px 14px 0 ${theme.palette.primary.main}40`,
                                        '&:hover': {
                                            bgcolor: theme.palette.primary.dark || '#4c2a6b',
                                            boxShadow: `0 6px 20px 0 ${theme.palette.primary.main}59`,
                                            transform: 'translateY(-1px)'
                                        },
                                        '&:disabled': {
                                            bgcolor: '#e5e7eb',
                                            color: '#9ca3af',
                                            transform: 'none',
                                            boxShadow: 'none'
                                        },
                                        transition: 'all 0.2s ease-in-out'
                                    }}
                                >
                                    Add Journal Entry
                                </Button>
                            </Box>

                            {/* Previous Entries */}
                            {internship?.journalEntries?.length > 0 && (
                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 600,
                                            color: '#1e293b',
                                            fontSize: '1.125rem',
                                            mb: 3
                                        }}
                                    >
                                        Previous Reflections ({internship.journalEntries.length})
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                        {internship.journalEntries.map((entry, index) => (
                                            <Box
                                                key={entry.id}
                                                sx={{
                                                    bgcolor: '#f8fafc',
                                                    border: '1px solid #e2e8f0',
                                                    borderRadius: '12px',
                                                    p: 3,
                                                    transition: 'all 0.2s ease-in-out',
                                                    '&:hover': {
                                                        bgcolor: '#f1f5f9',
                                                        borderColor: '#cbd5e1',
                                                        transform: 'translateY(-1px)',
                                                        boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.08)'
                                                    }
                                                }}
                                            >
                                                <Box sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    mb: 2
                                                }}>
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: '#374151'
                                                        }}
                                                    >
                                                        {new Date(entry.timestamp).toLocaleDateString('en-US', {
                                                            month: 'long',
                                                            day: 'numeric',
                                                            year: 'numeric'
                                                        })}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: '#6b7280',
                                                            fontSize: '0.75rem'
                                                        }}
                                                    >
                                                        {new Date(entry.timestamp).toLocaleTimeString()}
                                                    </Typography>
                                                </Box>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        color: '#374151',
                                                        lineHeight: 1.6,
                                                        fontSize: '0.95rem'
                                                    }}
                                                >
                                                    {entry.content}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Meeting Confirmation Modal */}
            <Dialog
                open={modalOpen}
                onClose={handleCloseModal}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        p: 2
                    }
                }}
            >
                <DialogTitle sx={{
                    fontWeight: 600,
                    fontSize: '1.25rem',
                    color: '#1e293b',
                    pb: 2
                }}>
                    Schedule Meeting
                </DialogTitle>

                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
                        <Box>
                            <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                                Selected Time Slot
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, color: '#1e293b' }}>
                                {selectedSlot.day} at {selectedSlot.time}
                            </Typography>
                        </Box>

                        <TextField
                            label="Meeting Title"
                            value={meetingTitle}
                            onChange={(e) => setMeetingTitle(e.target.value)}
                            fullWidth
                            variant="outlined"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '12px'
                                }
                            }}
                        />

                        <TextField
                            label="Meeting Date"
                            type="date"
                            value={meetingDate}
                            onChange={(e) => setMeetingDate(e.target.value)}
                            fullWidth
                            variant="outlined"
                            InputLabelProps={{
                                shrink: true,
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '12px'
                                }
                            }}
                        />

                        <Box sx={{
                            bgcolor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: '12px',
                            p: 3
                        }}>
                            <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                                Meeting Details
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#374151' }}>
                                • Google Meet invitation will be sent to all attendees
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#374151' }}>
                                • Calendar event will be created automatically
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#374151' }}>
                                • Attendees: {currentUser?.name}, {student?.name || 'Mentee'}
                            </Typography>
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 3, pt: 2 }}>
                    <Button
                        onClick={handleCloseModal}
                        sx={{
                            color: '#64748b',
                            fontWeight: 500,
                            textTransform: 'none',
                            px: 3,
                            py: 1
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmMeeting}
                        variant="contained"
                        disabled={!meetingTitle.trim() || !meetingDate || isSchedulingMeeting}
                        startIcon={isSchedulingMeeting ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{
                            bgcolor: theme.palette.primary.main,
                            color: 'white',
                            fontWeight: 600,
                            textTransform: 'none',
                            px: 4,
                            py: 1.25,
                            borderRadius: '12px',
                            '&:hover': {
                                bgcolor: theme.palette.primary.dark || '#4c2a6b'
                            },
                            '&:disabled': {
                                bgcolor: '#e5e7eb',
                                color: '#9ca3af'
                            }
                        }}
                    >
                        {isSchedulingMeeting ? 'Scheduling Meeting...' : 'Schedule Meeting'}
                    </Button>
                </DialogActions>
            </Dialog>
        </MainLayout>
    );
};

export default Internship;